import React, { useEffect, useMemo } from 'react';
import { theme } from 'antd';
import { EditorContent, useEditor } from '@tiptap/react';
import type { Editor } from '@tiptap/react';

import RichTextToolbar from './Toolbar';
import { buildEditorExtensions } from './extensions';
import {
	DEFAULT_IMAGE_ID_ATTR,
	useInlineImageSupport,
} from './inlineImageSupport';
import type {
	ImportImageUrlFn,
	UploadImageFn,
	UploadedImage,
} from './inlineImageSupport';

import './styles.css';

export type { ImportImageUrlFn, UploadImageFn, UploadedImage };

export interface RichTextEditorProps {
	/** Stored HTML. Treated as the initial value, not a controlled prop. */
	content: string;
	onChange: (html: string) => void;
	/**
	 * Stores a pasted, dropped or picked image and resolves to its id and URL.
	 * Omit it to keep the editor text-only.
	 */
	onUploadImage?: UploadImageFn;
	/**
	 * Stores an image the clipboard described by URL instead of handing over —
	 * how Google Docs and web pages paste. Without it those images are dropped,
	 * since hot-linking someone else's host does not survive.
	 */
	onImportImageUrl?: ImportImageUrlFn;
	/**
	 * `data-*` attribute the uploaded image id is written to, so the server can
	 * regenerate an expiring URL on read. Consumers with their own image library
	 * should pass their own attribute; see `DEFAULT_IMAGE_ID_ATTR` in
	 * `./inlineImageSupport` for the default.
	 */
	imageIdAttr?: string;
	placeholder?: string;
	minHeight?: number;
	editable?: boolean;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
	content,
	onChange,
	onUploadImage,
	onImportImageUrl,
	imageIdAttr = DEFAULT_IMAGE_ID_ATTR,
	placeholder = 'Type here... Paste images directly.',
	minHeight = 160,
	editable = true,
}) => {
	const { token } = theme.useToken();
	const images = useInlineImageSupport(
		onUploadImage,
		onImportImageUrl,
		imageIdAttr
	);

	const extensions = useMemo(
		() => buildEditorExtensions(images.extensions, placeholder),
		[images.extensions, placeholder]
	);

	const editor = useEditor({
		extensions,
		content: content || '',
		editable,
		onUpdate: ({ editor: instance }) => onChange(instance.getHTML()),
	});

	// The server sanitizes on write and refreshes image URLs on read, so a
	// refetch can legitimately differ from what was typed. Re-seed the document
	// only while the user is not in it, otherwise the caret jumps mid-sentence.
	useEffect(() => {
		if (!editor || editor.isFocused) return;
		if ((content || '') === editor.getHTML()) return;
		editor.commands.setContent(content || '', { emitUpdate: false });
	}, [content, editor]);

	return (
		<div
			className="rte"
			style={
				{
					'--rte-min-height': `${minHeight}px`,
					'--rte-radius': `${token.borderRadius}px`,
					'--rte-border': token.colorBorder,
				} as React.CSSProperties
			}
		>
			{editable && (
				<RichTextToolbar
					editor={editor}
					onUploadImage={onUploadImage}
					onInsertImageFile={(file) => images.uploadAndInsert(file, editor)}
				/>
			)}
			<EditorContent editor={editor} className="rte__content" />
		</div>
	);
};

/** Read-only render of editor HTML. Shares the editor's typography. */
export const RichTextViewer: React.FC<{
	html: string | null | undefined;
	emptyText?: string;
	className?: string;
}> = ({ html, emptyText = 'No content', className }) => {
	const isEmpty = !html || !html.replace(/<[^>]*>/g, '').trim();

	if (isEmpty && !html?.includes('<img')) {
		return (
			<div
				className={`rte-view rte-view--empty${
					className ? ` ${className}` : ''
				}`}
			>
				{emptyText}
			</div>
		);
	}

	return (
		<div
			className={`rte-view${className ? ` ${className}` : ''}`}
			dangerouslySetInnerHTML={{ __html: html as string }}
		/>
	);
};

/**
 * Adapts the editor to the `value`/`onChange` contract an antd `Form.Item`
 * passes to its child.
 */
export const RichTextFormField: React.FC<
	Omit<RichTextEditorProps, 'content' | 'onChange'> & {
		value?: string;
		onChange?: (html: string) => void;
	}
> = ({ value = '', onChange, ...rest }) => (
	<RichTextEditor
		content={value}
		onChange={(html) => onChange?.(html)}
		{...rest}
	/>
);

export type { Editor };
export default RichTextEditor;
